import React, { useState, useEffect } from "react";
import { X, Download, ExternalLink, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import documentAccessService from "@/services/documentAccessService.js";

const DocumentViewer = ({ document, isOpen, onClose, onDownload }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);

  // Helper function to check if file is an image
  const isImageFile = (fileName, fileType) => {
    if (!fileName && !fileType) return false;
    
    // Check file type first (MIME type)
    if (fileType && fileType.startsWith('image/')) {
      return true;
    }
    
    // Check file name extension
    if (fileName) {
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
      const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
      return imageExtensions.includes(extension);
    }
    
    return false;
  };

  useEffect(() => {
    if (isOpen && document) {
      loadDocumentUrl();
    }
  }, [isOpen, document]);

  const loadDocumentUrl = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const access = await documentAccessService.getDocumentAccess(document.id);
      setDocumentUrl(access.viewerUrl);
    } catch (err) {
      setError(err?.message || "Fehler beim Laden des Dokuments");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const access = await documentAccessService.getDocumentAccess(document.id, { forceRefresh: true });
      const link = window.document.createElement("a");
      link.href = access.viewerUrl;
      link.download = document.name;
      link.target = "_blank";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleExternalLink = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await documentAccessService.openDocument(document.id);
    } catch (err) {
      setError(err?.message || "Dokument konnte nicht geöffnet werden");
    }
  };

  if (!isOpen || !document) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-gray-900/75 dark:bg-black/75 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl max-h-[90vh] w-full mx-4 flex flex-col border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📄</span>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {document.name}
              </h2>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={handleDownload}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 border-gray-300 dark:border-gray-600"
            >
              <Download className="h-4 w-4" />
              Herunterladen
            </Button>
            
            <Button
              onClick={handleExternalLink}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 border-gray-300 dark:border-gray-600"
            >
              <ExternalLink className="h-4 w-4" />
              In neuem Tab öffnen
            </Button>
            
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
                <p className="text-gray-600 dark:text-gray-400">Dokument wird geladen...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <AlertCircle className="h-8 w-8 mx-auto mb-4 text-red-600" />
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </div>
            </div>
          ) : isImageFile(document.name, document.file_type) ? (
            <div className="flex items-center justify-center h-96 p-4">
              <img
                src={documentUrl}
                alt={document.name}
                className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                onError={() => setError("Bild konnte nicht geladen werden")}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="text-6xl mb-4">📄</div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {document.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Dokument bereit zum Download
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-4">
              <span>Kategorie: {document.category || "Keine"}</span>
              <span>Größe: {document.file_size ? `${(document.file_size / 1024).toFixed(1)} KB` : "Unbekannt"}</span>
            </div>
            <div>
              Hochgeladen: {document.created_at ? new Date(document.created_at).toLocaleDateString("de-DE") : "Unbekannt"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
